import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-score',
  templateUrl: './score.component.html',
  styleUrls: ['./score.component.scss'],
})
export class ScoreComponent implements OnInit, OnChanges {
  title = 'score';

  @Input() score = 10;
  @Input() isOnAnalysis!: boolean;
  @Input() isOnAi!: boolean;
  @Input() isOnManipulation!: boolean;
  @Input() docStatus: string = ' ';
  @Input() sideBarCollapsed: boolean = false;

  // Cached computed properties
  warningColor: string = '';
  indexImage: string = '';
  warningText: string = '';
  displayValue: string = '';
  percentage: number = 0;
  backgroundColor: string = '';

  // Thresholds
  highRiskThreshold: number = 80;
  mediumRiskThreshold: number = 70;

  originalityLevelClean: Record<string, string> = {};

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.computeScoreValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate only when relevant inputs change
    if (changes['docStatus'] || changes['score']) {
      this.computeScoreValues();
    }
  }

  private computeScoreValues(): void {
    this.getOriginalityScore();
    this.backgroundColor = this.computeBackgroundColor();
  }

  getOriginalityScore(): number {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const originalityDisplay = user?.Institution?.InstitutionSettings?.[0]?.originalityDisplay;
    this.highRiskThreshold = user?.Institution?.InstitutionSettings?.[0]?.originalityRiskFactorHighRisk || 80;
    this.mediumRiskThreshold = user?.Institution?.InstitutionSettings?.[0]?.originalityRiskFactorMediumRisk || 70;

    if (originalityDisplay === 'None') {
      this.displayValue = 'N/A';
      this.setWarningColor('#949494'); // Gray color for None state
      this.indexImage = './assets/report_v2/not_answered.png'; // Info icon for None state
      this.warningText = this.translate.instant('Originality Index is not available when the originality score display option for the report is set to None.');
      return 4; // Return 4 for None state
    }

    if (this.docStatus) {
      this.percentage = parseFloat(this.docStatus);

      if (originalityDisplay === 'Percentage') {
        this.displayValue = this.percentage.toFixed(2) + '%';
      } else {
        if (this.percentage >= this.highRiskThreshold) {
          this.displayValue = this.translate.instant('new_entries.high_risk');
        } else if (this.percentage >= this.mediumRiskThreshold) {
          this.displayValue = this.translate.instant('new_entries.medium_risk');
        } else {
          this.displayValue = this.translate.instant('new_entries.low_risk');
        }
      }

      if (this.percentage >= this.highRiskThreshold) {
        this.setWarningColor('#A50D0D');
        this.indexImage = '../../../../../../assets/report_v2/warning-circled-filled.svg';
        this.warningText = this.translate.instant('new_entries.high_risk_description');
        return 1;
      } else if (this.percentage >= this.mediumRiskThreshold) {
        this.setWarningColor('#fffb00');
        this.indexImage = '../../../../../../assets/report_v2/warning-triangle-fillled.svg';
        this.warningText = this.translate.instant('new_entries.medium_risk_description');
        return 2;
      } else {
        this.setWarningColor('#269e44');
        this.indexImage = '../../../../../../assets/report_v2/check-circled-filled.svg';
        this.warningText = this.translate.instant('new_entries.low_risk_description');
        return 3;
      }
    } else {
      return 0;
    }
  }

  private setWarningColor(color: string): void {
    this.warningColor = color;
  }

  private computeBackgroundColor(): string {
    if (this.warningColor) {
      const rgba = this.warningColor.match(/[0-9a-fA-F]{2}/g)?.map((hex) => parseInt(hex, 16));
      return `rgba(${rgba?.join(',')}, 0.2)`;
    } else {
      return `rgba(0, 0, 0, 0.2)`;
    }
  }

  openLink(link: string): void {
    window.open(link, '_blank');
  }
}
